import { useContext, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";
import { validateNumberOrBlock } from "../../utils/validate-number-or-block";

import type {
  GetPrefixRequestBody,
  GetPrefixSuccessResponse,
  NumberCheckResponseSuccessResponse,
} from "@sperrdatei/dto/types";

type CheckNumberFormProps = {
  setSearchedNumber: (number: string) => void;
  setFoundNumber: (
    number: NumberCheckResponseSuccessResponse | null | undefined,
  ) => void;
};

export const CheckNumberForm: React.FC<CheckNumberFormProps> = ({
  setSearchedNumber,
  setFoundNumber,
}: CheckNumberFormProps) => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [number, setNumber] = useState("");
  const [numberError, setNumberError] = useState("");

  const checkNumber = async (): Promise<void> => {
    clearForm();
    setFoundNumber(undefined);
    setSearchedNumber(number);

    const numberValidationresult = validateNumberOrBlock(number, false);
    if (!numberValidationresult.isValid) {
      setNumberError(numberValidationresult.errorMessage);
      return;
    }

    const body: GetPrefixRequestBody = { number };
    const getPrefixResponse = await apiFetch("/api/number/prefix", {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!getPrefixResponse.ok) {
      setNumberError("Die Vorwahl ist unbekannt.");
      return;
    }

    const { prefix } =
      (await getPrefixResponse.json()) as GetPrefixSuccessResponse;
    const numberWithoutPrefix = number.replace(prefix, "");

    if (numberWithoutPrefix.length < 3) {
      setNumberError("Die minimale Länge der Rufnummer beträgt 3 Ziffern.");
      return;
    }

    if (numberWithoutPrefix.length > 10) {
      setNumberError("Die maximale Länge der Rufnummer beträgt 10 Ziffern.");
      return;
    }

    const response = await apiFetch(`/api/number/${number}`);

    if (response.ok) {
      setFoundNumber(await response.json());
      return;
    }

    if (response.status === 404) {
      setFoundNumber(null);
      return;
    }

    navigate("/error");
    return;
  };

  const clearForm = (): void => {
    setNumber("");
    setFoundNumber(undefined);
  };

  return (
    <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          checkNumber();
        }}
        noValidate
      >
        <div className="row">
          <div className="mb-2">
            <label htmlFor="number" className="form-text">
              <strong>Nummer</strong>
            </label>
            <input
              type="text"
              id="number"
              maxLength={15}
              value={number}
              className={
                numberError ? "form-control is-invalid" : "form-control "
              }
              onChange={(e) => setNumber(e.target.value)}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(
                  /[^0-9x*]/g,
                  "",
                );
              }}
              required
            />
            <div id="numberFeedback" className="invalid-feedback">
              {numberError}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-primary mb-3"
          onClick={() => checkNumber()}
        >
          Prüfen
        </button>
      </form>
    </div>
  );
};
